// The item window's Pricing table, laid out like commonsku (Stephanie
// 2026-10-14: "make it more like commonsku's"): one table — Color/Size, Qty,
// Net cost, Margin, Retail (with the lock), Total — and the item's added
// costs as more rows in the same table (passed in as `children`). No repeated
// photo / name / description, no tax column, no charge bands: decoration
// charges live under their location, and the item summary holds the totals.
//
// Money: no new math. Row totals and margins use the shared module; a typed
// margin prices the row with sellPriceFromCostMills; Retail unlocked keeps the
// margin on a cost change with keepMarginSellPrice. Writes go through the same
// onPatchLine / onPatchVariant mutations the quote page uses.
//
// Rows ARE the colours/sizes (Stephanie 2026-11-04: "i dont like this
// workflow. putting in the total and then choosing each one to contribute to
// the total… Make it more like CS"). Each row's Color / Size cell is a
// dropdown of the product's own combinations ("Blue / 512MB", or "Other…" to
// type one); each row has its own qty, cost, margin, retail and × to remove.
// "+ Add color / size" adds a row; the item's quantity is the sum of the rows
// (the server's roll-up). A new row has no cost / price of its own, so it
// shows and bills the item's — never a made-up number. Same variant
// endpoints (POST / PATCH / DELETE) the quote page's grid uses.

import { useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { Lock, LockOpen, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import type { ColourOption } from "@/components/orders/SizeBreakdownEditor";
import {
  EditableCell,
  isValidCost,
  isValidCostOrBlank,
  isValidMargin,
  isValidMoney,
  isValidMoneyOrBlank,
  isValidQuantity,
} from "./DocumentLineCard";
import {
  costInputValue,
  formatCents,
  keepMarginSellPrice,
  marginHundredths,
  normalizeMoneyInput,
  normalizeQuantityInput,
  sellPriceFromCostMills,
  toCents,
  toMills,
} from "./money";
import { docLineTitle, docVariantLabel, type DocLine } from "./types";
import { cn } from "@/lib/utils";

/** One grid for product rows AND added-cost rows, so they line up:
 *  [remove] [name] [qty] [net cost] [margin] [retail] [total]. */
export const PRICING_COLS =
  "grid grid-cols-[28px_minmax(0,1fr)_110px_120px_100px_120px_130px] items-center gap-3";

/** Margin of one row, as a 2dp string for its box ("" when not computable). */
function rowMargin(priceCents: number, costMills: number): string {
  if (priceCents <= 0 || costMills <= 0) return "";
  return (((priceCents * 10 - costMills) / (priceCents * 10)) * 100).toFixed(2);
}

/** One colour / size choice. "" = none (a one-size item, or sizes only). */
export interface ColorSize {
  color: string;
  size: string;
}

const comboKey = (c: ColorSize): string => `${c.color.trim().toLowerCase()}\u0000${c.size.trim().toLowerCase()}`;
const comboLabel = (c: ColorSize): string => [c.color, c.size].filter((p) => p.trim() !== "").join(" / ");
const OTHER = "__other__";
const NO_PICK: ColorSize = { color: "", size: "" };

/** Every colour × size the product comes in ("Blue / 512MB"); just the
 *  colour when it has no sizes. Pure. */
export function colorSizeOptions(colours: readonly ColourOption[]): ColorSize[] {
  const out: ColorSize[] = [];
  for (const c of colours) {
    if (c.sizes.length === 0) out.push({ color: c.name, size: "" });
    else for (const s of c.sizes) out.push({ color: c.name, size: s });
  }
  return out;
}

/** The Color / Size cell: a dropdown of the product's combinations, with
 *  "Other…" to type a colour and size the catalogue doesn't list. A custom
 *  item (no catalogue options) gets the two boxes straight away. */
function ColorSizePicker(props: {
  value: ColorSize;
  /** Extra text after the label (a row's material). */
  suffix?: string;
  options: ColorSize[];
  /** Combinations other rows already have. */
  taken: Set<string>;
  ariaLabel: string;
  disabled?: boolean;
  onPick: (next: ColorSize) => void;
}) {
  const { value, suffix, options, taken, ariaLabel, disabled, onPick } = props;
  const [typing, setTyping] = useState(false);
  const [color, setColor] = useState(value.color);
  const [size, setSize] = useState(value.size);
  useEffect(() => {
    setColor(value.color);
    setSize(value.size);
  }, [value.color, value.size]);

  if (options.length === 0 || typing) {
    const commit = (): void => {
      const next = { color: color.trim(), size: size.trim() };
      if (options.length > 0) setTyping(false);
      if (comboKey(next) !== comboKey(value) && (next.color !== "" || next.size !== "")) onPick(next);
      else {
        setColor(value.color);
        setSize(value.size);
      }
    };
    const keys = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Enter") e.currentTarget.blur();
      if (e.key === "Escape" && options.length > 0) {
        setColor(value.color);
        setSize(value.size);
        setTyping(false);
      }
    };
    const box =
      "h-8 min-w-0 flex-1 rounded-md border border-brown-100 bg-white px-2 text-[15px] text-brown-500 focus:border-peach-400 focus:outline-none";
    return (
      <span
        className="flex min-w-0 items-center gap-1"
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commit();
        }}
      >
        <input
          autoFocus={typing}
          value={color}
          disabled={disabled}
          onChange={(e) => setColor(e.target.value)}
          onKeyDown={keys}
          placeholder="Color"
          maxLength={120}
          aria-label={`${ariaLabel}: color`}
          className={box}
        />
        <input
          value={size}
          disabled={disabled}
          onChange={(e) => setSize(e.target.value)}
          onKeyDown={keys}
          placeholder="Size"
          maxLength={60}
          aria-label={`${ariaLabel}: size`}
          className={box}
        />
      </span>
    );
  }

  // A Colour dropdown and a Size dropdown (Stephanie 2026-11-04: "the colors
  // should be a dropdown with an option to use a 1 time custom color") — each
  // with a one-off "Custom…" that turns that box into a text box for this row.
  return (
    <SplitColorSizePicker value={value} suffix={suffix} options={options} taken={taken} ariaLabel={ariaLabel} disabled={disabled} onPick={onPick} />
  );
}

const CUSTOM = "__custom__";

/** PURE — the colours a product lists, in order, and the sizes each comes in. */
export function splitOptions(options: readonly ColorSize[]): { colours: string[]; sizesFor: (colour: string) => string[]; hasSizes: boolean } {
  const colours: string[] = [];
  for (const o of options) if (o.color && !colours.includes(o.color)) colours.push(o.color);
  const allSizes: string[] = [];
  for (const o of options) if (o.size && !allSizes.includes(o.size)) allSizes.push(o.size);
  const sizesFor = (colour: string): string[] => {
    if (!colour || !colours.includes(colour)) return allSizes;
    const out: string[] = [];
    for (const o of options) if (o.color === colour && o.size && !out.includes(o.size)) out.push(o.size);
    return out;
  };
  return { colours, sizesFor, hasSizes: allSizes.length > 0 };
}

function SplitColorSizePicker(props: {
  value: ColorSize;
  suffix?: string;
  options: ColorSize[];
  taken: Set<string>;
  ariaLabel: string;
  disabled?: boolean;
  onPick: (next: ColorSize) => void;
}) {
  const { value, suffix, options, taken, ariaLabel, disabled, onPick } = props;
  const { colours, sizesFor, hasSizes } = splitOptions(options);
  const [customColour, setCustomColour] = useState(false);
  const [customSize, setCustomSize] = useState(false);
  const [colourText, setColourText] = useState(value.color);
  const [sizeText, setSizeText] = useState(value.size);
  useEffect(() => {
    setColourText(value.color);
    setSizeText(value.size);
  }, [value.color, value.size]);
  const sizes = sizesFor(value.color);
  const box = "h-8 min-w-0 rounded-md border border-brown-100 bg-white px-2 text-[15px] text-brown-500 focus:border-peach-400 focus:outline-none";
  // The colour gets the room ("Red – PMS 485C" must read in full); sizes are short.
  const colourSel = `${box} flex-[3]`;
  const sel = `${box} flex-[2]`;
  const pick = (next: ColorSize): void => {
    if (comboKey(next) !== comboKey(value)) onPick(next);
  };
  const colourBox =
    // A typed colour that isn't on the list stays a dropdown: it shows as its
    // own choice at the top (Stephanie 2026-11-04: "they arent drop downs anymore").
    customColour || colours.length === 0 ? (
      <input
        autoFocus={customColour}
        value={colourText}
        disabled={disabled}
        onChange={(e) => setColourText(e.target.value)}
        onBlur={() => {
          const c = colourText.trim();
          if (c === "") { setColourText(value.color); setCustomColour(false); return; }
          setCustomColour(false);
          pick({ color: c, size: value.size });
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { setColourText(value.color); setCustomColour(false); }
        }}
        placeholder="Custom color"
        maxLength={120}
        aria-label={`${ariaLabel}: custom color`}
        className={colourSel}
      />
    ) : (
      <select
        value={value.color}
        disabled={disabled}
        aria-label={`${ariaLabel}: color`}
        onChange={(e) => {
          if (e.target.value === CUSTOM) { setColourText(""); setCustomColour(true); return; }
          const c = e.target.value;
          const ok = sizesFor(c);
          pick({ color: c, size: value.size && (ok.length === 0 || ok.includes(value.size)) ? value.size : "" });
        }}
        className={colourSel}
      >
        {value.color === "" && <option value="">Choose color…</option>}
        {value.color !== "" && !colours.includes(value.color) && <option value={value.color}>{value.color}</option>}
        {colours.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
        <option value={CUSTOM}>Custom color…</option>
      </select>
    );
  const sizeBox = !hasSizes && !value.size ? null : customSize || !hasSizes ? (
    <input
      autoFocus={customSize}
      value={sizeText}
      disabled={disabled}
      onChange={(e) => setSizeText(e.target.value)}
      onBlur={() => {
        const z = sizeText.trim();
        setCustomSize(false);
        if (z === "") { setSizeText(value.size); return; }
        pick({ color: value.color, size: z });
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setSizeText(value.size); setCustomSize(false); }
      }}
      placeholder="Custom size"
      maxLength={60}
      aria-label={`${ariaLabel}: custom size`}
      className={sel}
    />
  ) : (
    <select
      value={value.size}
      disabled={disabled}
      aria-label={`${ariaLabel}: size`}
      onChange={(e) => {
        if (e.target.value === CUSTOM) { setSizeText(""); setCustomSize(true); return; }
        pick({ color: value.color, size: e.target.value });
      }}
      className={sel}
    >
      {value.size === "" && <option value="">Choose size…</option>}
      {value.size !== "" && !sizes.includes(value.size) && <option value={value.size}>{value.size}</option>}
      {sizes.map((z) => {
        const k = comboKey({ color: value.color, size: z });
        return (
          <option key={z} value={z} disabled={z !== value.size && taken.has(k)}>{z}</option>
        );
      })}
      <option value={CUSTOM}>Custom size…</option>
    </select>
  );
  return (
    <span className="flex min-w-0 items-center gap-1" title={suffix}>
      {colourBox}
      {sizeBox}
    </span>
  );
}

/** A row added with "+ Add color / size" that has no quantity yet. */
interface DraftRow {
  id: number;
  pick: ColorSize;
  qty: string;
  saving: boolean;
}

/** PURE — the row's choice from its saved note: "White swivel" → "White";
 *  nothing (or anything else) → the default, the first choice. */
export function rowChoiceOf(material: string | null | undefined, opt: { label: string; choices: string[] }): string {
  const m = (material ?? "").trim().toLowerCase();
  return opt.choices.find((c, i) => i > 0 && m === `${c} ${opt.label}`.toLowerCase()) ?? opt.choices[0]!;
}

/** PURE — what a choice saves: the default saves nothing, another "White swivel". */
export function rowMaterialFor(choice: string, opt: { label: string; choices: string[] }): string | null {
  return choice === opt.choices[0] ? null : `${choice} ${opt.label.toLowerCase()}`;
}

export function ItemPricingTable(props: {
  orderId: string;
  line: DocLine;
  currency: string;
  retailLocked: boolean;
  onToggleLock: () => void;
  onPatchLine: (body: Record<string, unknown>) => void;
  onPatchVariant: (variantId: string, body: Record<string, unknown>) => void;
  /** The product's colours / sizes, for each row's dropdown. */
  colours: ColourOption[];
  /** A second dropdown on each row — the Venice swivel (Stephanie 2026-11-04:
   *  "the body color and another drop down for swivel"). The first choice is
   *  the default and saves as nothing; another saves as "<choice> <label>". */
  rowOption?: { label: string; choices: string[] } | null;
  /** "+ Add color / size" was clicked: add an empty row. */
  addingRow: boolean;
  onDoneAddingRow: () => void;
  detailQueryKey: QueryKey;
  /** Added-cost rows, rendered in this table under the product rows. */
  children?: React.ReactNode;
}) {
  const {
    orderId,
    line,
    currency,
    retailLocked,
    onToggleLock,
    onPatchLine,
    onPatchVariant,
    colours,
    rowOption = null,
    addingRow,
    onDoneAddingRow,
    detailQueryKey,
    children,
  } = props;
  const linePriceCents = toCents(line.unitPrice);
  const lineCostMills = toMills(line.netCost ?? 0);
  const queryClient = useQueryClient();
  const options = colorSizeOptions(colours);
  const base = `/orders/${orderId}/lines/${line.id}/variants`;
  const product = line.serviceType === "product";

  // Rows added but not saved yet — saved once they have a quantity (the
  // server needs one, and nothing is filled in for the rep).
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const nextDraftId = useRef(1);
  useEffect(() => {
    if (!addingRow) return;
    setDrafts((ds) => [...ds, { id: nextDraftId.current++, pick: NO_PICK, qty: "", saving: false }]);
    onDoneAddingRow();
  }, [addingRow, onDoneAddingRow]);

  // An item with no colours yet whose quantity is 0: the colour picked on its
  // one row, saved as the row once a quantity is typed.
  const [basePick, setBasePick] = useState<ColorSize | null>(null);
  // The one row became a colour/size row, but the fresh read hasn't landed yet.
  const baseConverted = useRef(false);
  useEffect(() => {
    if (line.variants.length > 0) baseConverted.current = false;
  }, [line.variants.length]);

  const [error, setError] = useState<string | null>(null);
  // One write at a time, each followed by a fresh read of the item.
  const queue = useRef<Promise<void>>(Promise.resolve());
  const run = (work: () => Promise<void>): Promise<void> => {
    const next = queue.current.then(async () => {
      try {
        await work();
        setError(null);
      } catch (err) {
        setError(
          err instanceof ApiError && err.status === 409
            ? "Couldn't save: that color / size is already a row, or this quote is locked."
            : "Couldn't save that row. Try again.",
        );
        throw err;
      } finally {
        await queryClient.refetchQueries({ queryKey: detailQueryKey }).catch(() => undefined);
      }
    });
    queue.current = next.catch(() => undefined);
    return next;
  };
  const post = (pick: ColorSize | null, quantity: number, position: number): Promise<unknown> =>
    api(base, {
      method: "POST",
      body: JSON.stringify({
        color: pick?.color.trim() || undefined,
        size: pick?.size.trim() || undefined,
        quantity,
        position,
      }),
    });
  /** An item with no colours yet: its one row (qty, no colour) becomes the
   *  first colour/size row, so the item's quantity stays the sum of rows. */
  const convertBase = async (): Promise<number> => {
    if (line.variants.length > 0 || baseConverted.current || line.quantityTotal <= 0) return 0;
    await post(basePick, line.quantityTotal, 0);
    baseConverted.current = true;
    setBasePick(null);
    return 1;
  };

  /** Combinations already on another row (so the dropdown greys them out). */
  const takenExcept = (skipVariant: string | null, skipDraft: number | null, skipBase = false): Set<string> => {
    const t = new Set<string>();
    for (const v of line.variants)
      if (v.id !== skipVariant && !v.material) t.add(comboKey({ color: v.color ?? "", size: v.size ?? "" }));
    for (const d of drafts) if (d.id !== skipDraft && comboLabel(d.pick) !== "") t.add(comboKey(d.pick));
    if (!skipBase && line.variants.length === 0 && basePick) t.add(comboKey(basePick));
    return t;
  };
  const alreadyARow = (pick: ColorSize, taken: Set<string>): boolean => {
    if (!taken.has(comboKey(pick))) return false;
    setError(`${comboLabel(pick)} is already a row. Change its quantity there.`);
    return true;
  };

  const saveDraft = (d: DraftRow): void => {
    const qty = Math.floor(Number(normalizeQuantityInput(d.qty)) || 0);
    if (qty <= 0 || d.saving) return;
    setDrafts((ds) => ds.map((x) => (x.id === d.id ? { ...x, saving: true } : x)));
    const position = line.variants.length + 1;
    void run(async () => {
      const added = await convertBase();
      await post(d.pick, qty, position + added);
      setDrafts((ds) => ds.filter((x) => x.id !== d.id));
    }).catch(() => setDrafts((ds) => ds.map((x) => (x.id === d.id ? { ...x, saving: false } : x))));
  };

  const head = "text-xs font-bold uppercase tracking-wide text-brown-300";
  const removeButton = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Remove ${label}`}
      title="Remove this row"
      className="flex justify-center text-brown-300 hover:text-rust-600"
    >
      <X className="h-4 w-4" />
    </button>
  );
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[820px]">
        <div className={cn(PRICING_COLS, "border-b border-brown-100 bg-cream-50 px-1 py-2", head)}>
          <span />
          <span>Color / Size</span>
          <span className="text-right">Qty</span>
          <span className="text-right">Net cost</span>
          <span className="text-right">Margin</span>
          <span className="flex items-center justify-end gap-1">
            Retail
            <button
              type="button"
              onClick={onToggleLock}
              aria-pressed={retailLocked}
              aria-label={retailLocked ? "Unlock retail" : "Lock retail"}
              title={
                retailLocked
                  ? "Retail locked: changing a cost keeps the price, the margin moves. Click to unlock."
                  : "Retail unlocked: changing a cost keeps the margin and re-prices. Click to lock."
              }
              className={retailLocked ? "text-rust-500" : "text-brown-300 hover:text-brown-500"}
            >
              {retailLocked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
            </button>
          </span>
          <span className="text-right">Total</span>
        </div>

        {line.variants.length > 0 ? (
          line.variants.map((v) => {
            const label = docVariantLabel(v);
            const priced = v.unitPrice != null && v.unitPrice !== "";
            const priceCents = priced ? toCents(v.unitPrice) : linePriceCents;
            const costed = v.unitCost != null && v.unitCost !== "";
            const costMills = costed ? toMills(v.unitCost) : lineCostMills;
            return (
              <div key={v.id} className={cn(PRICING_COLS, "border-b border-brown-50 px-1 py-2 text-[15px] tabular-nums")}>
                {removeButton(label, () => void run(async () => void (await api(`${base}/${v.id}`, { method: "DELETE" }))).catch(() => undefined))}
                <div className={rowOption ? "flex min-w-0 items-center gap-2" : "contents"}>
                <ColorSizePicker
                  value={{ color: v.color ?? "", size: v.size ?? "" }}
                  suffix={rowOption ? undefined : (v.material ?? undefined)}
                  options={options}
                  taken={takenExcept(v.id, null)}
                  ariaLabel={`Color / size for ${label}`}
                  onPick={(pick) => {
                    if (alreadyARow(pick, takenExcept(v.id, null))) return;
                    void run(async () =>
                      void (await api(`${base}/${v.id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ color: pick.color || null, size: pick.size || null }),
                      })),
                    ).catch(() => undefined);
                  }}
                />
                {rowOption && (
                  <select
                    aria-label={`${rowOption.label} for ${label}`}
                    value={rowChoiceOf(v.material, rowOption)}
                    onChange={(e) => {
                      const material = rowMaterialFor(e.target.value, rowOption);
                      void run(async () =>
                        void (await api(`${base}/${v.id}`, { method: "PATCH", body: JSON.stringify({ material }) })),
                      ).catch(() => undefined);
                    }}
                    className="h-9 shrink-0 rounded-md border border-rule bg-white px-2 text-sm text-ink"
                  >
                    {rowOption.choices.map((c) => (
                      <option key={c} value={c}>{c} {rowOption.label.toLowerCase()}</option>
                    ))}
                  </select>
                )}
                </div>
                <EditableCell
                  value={String(v.quantity)}
                  ariaLabel={`Quantity for ${label}`}
                  validate={isValidQuantity}
                  onCommit={(next) => onPatchVariant(v.id, { quantity: Number(normalizeQuantityInput(next)) })}
                />
                <EditableCell
                  value={costed ? costInputValue(v.unitCost) : costInputValue(line.netCost)}
                  prefix="$"
                  ariaLabel={`Cost for ${label}`}
                  validate={isValidCostOrBlank}
                  onCommit={(next) => {
                    const c = normalizeMoneyInput(next);
                    const body: Record<string, unknown> = { unit_cost: c === "" ? null : c };
                    if (!retailLocked) {
                      const sell = keepMarginSellPrice(costMills, priceCents, c === "" ? lineCostMills : toMills(c));
                      if (sell != null) body.unit_price = sell;
                    }
                    onPatchVariant(v.id, body);
                  }}
                />
                <EditableCell
                  value={rowMargin(priceCents, costMills)}
                  ariaLabel={`Margin for ${label}`}
                  validate={(m) => isValidMargin(m) && costMills > 0}
                  onCommit={(m) => {
                    const h = marginHundredths(m.trim().replace(/%$/, ""));
                    const sell = h == null ? null : sellPriceFromCostMills(costMills, h);
                    if (sell != null) onPatchVariant(v.id, { unit_price: sell });
                  }}
                />
                <EditableCell
                  value={priced ? String(v.unitPrice) : line.unitPrice}
                  prefix="$"
                  ariaLabel={`Retail for ${label}`}
                  validate={isValidMoneyOrBlank}
                  onCommit={(next) => {
                    const p = normalizeMoneyInput(next);
                    onPatchVariant(v.id, { unit_price: p === "" ? null : p });
                  }}
                />
                <span className="text-right font-semibold text-brown-500">
                  {formatCents(priceCents * v.quantity, currency)}
                </span>
              </div>
            );
          })
        ) : (
          <div className={cn(PRICING_COLS, "border-b border-brown-50 px-1 py-2 text-[15px] tabular-nums")}>
            <span />
            {product ? (
              <ColorSizePicker
                value={basePick ?? NO_PICK}
                options={options}
                taken={takenExcept(null, null, true)}
                ariaLabel={`Color / size for ${docLineTitle(line)}`}
                onPick={(pick) => {
                  if (alreadyARow(pick, takenExcept(null, null, true))) return;
                  // No quantity yet: hold the pick until one is typed.
                  if (line.quantityTotal <= 0) {
                    setBasePick(pick);
                    return;
                  }
                  void run(async () => {
                    if (baseConverted.current) return;
                    await post(pick, line.quantityTotal, 0);
                    baseConverted.current = true;
                  }).catch(() => undefined);
                }}
              />
            ) : (
              <span className="truncate text-brown-500">{docLineTitle(line)}</span>
            )}
            <EditableCell
              value={String(line.quantityTotal)}
              ariaLabel="Quantity"
              validate={isValidQuantity}
              onCommit={(next) => {
                const qty = Number(normalizeQuantityInput(next));
                // A colour picked before there was a quantity becomes the row.
                if (basePick && qty > 0) {
                  const pick = basePick;
                  void run(async () => {
                    await post(pick, qty, 0);
                    baseConverted.current = true;
                    setBasePick(null);
                  }).catch(() => undefined);
                } else onPatchLine({ quantity_total: qty });
              }}
            />
            <EditableCell
              value={costInputValue(line.netCost)}
              prefix="$"
              ariaLabel="Net cost"
              validate={isValidCost}
              onCommit={(next) => {
                const c = normalizeMoneyInput(next);
                const body: Record<string, unknown> = { net_cost: c };
                if (!retailLocked) {
                  const sell = keepMarginSellPrice(lineCostMills, linePriceCents, toMills(c));
                  if (sell != null) body.unit_price = sell;
                }
                onPatchLine(body);
              }}
            />
            <EditableCell
              value={rowMargin(linePriceCents, lineCostMills)}
              ariaLabel="Margin"
              validate={(m) => isValidMargin(m) && lineCostMills > 0}
              onCommit={(m) => {
                const h = marginHundredths(m.trim().replace(/%$/, ""));
                const sell = h == null ? null : sellPriceFromCostMills(lineCostMills, h);
                if (sell != null) onPatchLine({ unit_price: sell });
              }}
            />
            <EditableCell
              value={line.unitPrice}
              prefix="$"
              ariaLabel="Retail"
              validate={isValidMoney}
              onCommit={(next) => onPatchLine({ unit_price: normalizeMoneyInput(next) })}
            />
            <span className="text-right font-semibold text-brown-500">
              {formatCents(linePriceCents * line.quantityTotal, currency)}
            </span>
          </div>
        )}

        {/* New rows: pick the color / size and type a quantity; it saves then.
            Until it has its own, a row uses the item's cost and retail. */}
        {drafts.map((d) => {
          const qty = Math.floor(Number(normalizeQuantityInput(d.qty)) || 0);
          const muted = "text-right text-brown-300";
          const margin = rowMargin(linePriceCents, lineCostMills);
          return (
            <div key={`draft-${d.id}`} className={cn(PRICING_COLS, "border-b border-brown-50 px-1 py-2 text-[15px] tabular-nums")}>
              {removeButton("this row", () => setDrafts((ds) => ds.filter((x) => x.id !== d.id)))}
              <ColorSizePicker
                value={d.pick}
                options={options}
                taken={takenExcept(null, d.id)}
                ariaLabel="Color / size for the new row"
                disabled={d.saving}
                onPick={(pick) => {
                  if (alreadyARow(pick, takenExcept(null, d.id))) return;
                  const next = { ...d, pick };
                  setDrafts((ds) => ds.map((x) => (x.id === d.id ? next : x)));
                  saveDraft(next);
                }}
              />
              <EditableCell
                value={d.qty}
                ariaLabel="Quantity for the new row"
                validate={isValidQuantity}
                disabled={d.saving}
                onCommit={(next) => {
                  const nd = { ...d, qty: next };
                  setDrafts((ds) => ds.map((x) => (x.id === d.id ? nd : x)));
                  saveDraft(nd);
                }}
              />
              <span className={muted}>${costInputValue(line.netCost)}</span>
              <span className={muted}>{margin}</span>
              <span className={muted}>${line.unitPrice}</span>
              <span className={muted}>{d.saving ? "Saving…" : formatCents(linePriceCents * qty, currency)}</span>
            </div>
          );
        })}

        {children}
      </div>
      {error && <p className="mt-2 text-sm text-rust-600">{error}</p>}
    </div>
  );
}
